import { useState } from 'react';
import { GraduationCap } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { useAcademicMentors, useSetAcademicTutor } from '@/lib/tutor/rendezvous';

interface AcademicTutorControlProps {
  userId: string;
  currentTutorId: string | null;
  currentTutorName: string | null;
}

/**
 * Choix du tuteur académique par un étudiant alternant (parmi les tuteurs
 * offers_academic_mentoring=true). Settable seulement si is_alternant=true,
 * enforcé côté DB par le trigger guard_academic_tutor_assignment — ce
 * composant n'est monté que pour un profil is_alternant (cf. _app.profile.tsx).
 */
export function AcademicTutorControl({
  userId,
  currentTutorId,
  currentTutorName,
}: AcademicTutorControlProps) {
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('');
  const { data: mentors = [], isLoading } = useAcademicMentors(query);
  const setTutor = useSetAcademicTutor(userId);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <span className="text-ink-600 text-sm">Tuteur académique</span>
        <div className="flex items-center gap-2">
          <span className="text-ink-900 text-sm font-semibold">{currentTutorName ?? 'Aucun'}</span>
          {!searching && (
            <Button type="button" variant="outline" size="sm" onClick={() => setSearching(true)}>
              {currentTutorId ? 'Changer' : 'Choisir'}
            </Button>
          )}
        </div>
      </div>

      {searching && (
        <div className="border-ink-100 space-y-3 rounded-xl border bg-white p-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un tuteur…"
            className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-11 w-full rounded-xl border px-3.5 font-sans text-sm transition-colors focus:outline-none"
          />

          {isLoading && <p className="text-ink-400 font-sans text-xs">Recherche…</p>}

          {!isLoading && mentors.length === 0 && (
            <p className="text-ink-500 font-sans text-xs">
              Aucun tuteur ne propose de mentorat académique pour l'instant.
            </p>
          )}

          <div className="space-y-1.5">
            {mentors.map((m) => (
              <button
                key={m.tutorId}
                type="button"
                onClick={async () => {
                  await setTutor.mutateAsync(m.tutorId);
                  setSearching(false);
                }}
                disabled={setTutor.isPending}
                className={cn(
                  'flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors active:scale-[0.98]',
                  currentTutorId === m.tutorId
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-ink-100 hover:border-primary-300 bg-white',
                )}
              >
                <div className="bg-primary-50 flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
                  <GraduationCap className="text-primary-600 h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-ink-900 truncate text-sm font-semibold">{m.displayName}</p>
                  {m.bio && <p className="text-ink-500 truncate text-xs">{m.bio}</p>}
                </div>
              </button>
            ))}
          </div>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => setSearching(false)}
          >
            Fermer
          </Button>
        </div>
      )}
    </div>
  );
}
